/**
 * Trending sounds accessor.
 *
 * Reads pre-aggregated 7-day download velocity scores from the
 * `trending_sounds` table (populated nightly by app/api/cron/trending).
 *
 * Fallback: when the table is empty or unavailable, falls back to
 * downloads-desc from the `sounds` table so the rail always renders
 * something useful on first deploy (before the first cron run).
 *
 * ISR safety: throws on fetch error rather than returning [] silently.
 * Silent return-[] would poison a prerendered ISR page's cache with an
 * empty rail. The caller (app/page.tsx) uses Promise.allSettled so a
 * thrown error is caught and Sentry-reported without crashing the page.
 */

import { supabase, Sentry, SupabaseError, withRetry } from './shared'
import type { Sound } from '../supabase-types'
import { LIST_COLUMNS } from './sounds'
import { EXCLUDE_MATURE_FILTER } from '../mature-content'

export interface TrendingSound {
  sound_id: string
  downloads_7d: number
  score: number
  computed_at: string
  sound: Sound
}

export const trendingApi = {
  /**
   * Returns up to `limit` trending sounds ordered by velocity score desc.
   *
   * Throws on Supabase error (ISR-safe — callers should use
   * Promise.allSettled and report via Sentry).
   *
   * Falls back to top-downloads ordering when `trending_sounds` is empty.
   */
  async getTrendingSounds(limit: number = 6): Promise<Sound[]> {
    return withRetry(async () => {
      if (!supabase) throw new Error('Supabase client is not initialized')

      // 1. Try the trending_sounds table (populated by cron).
      //    Join to sounds via the FK so we get full Sound objects back.
      const { data: trendingRows, error: trendingError } = await supabase
        .from('trending_sounds')
        .select(`score, downloads_7d, computed_at, sound:sounds!inner(${LIST_COLUMNS})`)
        .eq('sounds.is_active', true)
        .or(EXCLUDE_MATURE_FILTER, { referencedTable: 'sounds' })
        .order('score', { ascending: false })
        .limit(limit)

      if (trendingError) {
        // Throw so ISR page doesn't cache an empty rail.
        throw new SupabaseError(
          `Failed to fetch trending sounds: ${trendingError.message}`,
          trendingError,
        )
      }

      // 2. If the table is empty (first deploy, migration not yet applied, or
      //    cron hasn't run), fall back to all-time downloads-desc.
      if (!trendingRows || trendingRows.length === 0) {
        return fetchFallbackSounds(limit)
      }

      // 3. Extract the joined Sound objects; filter out any null joins
      //    (orphaned trending rows pointing to deleted sounds).
      //    Supabase returns the joined relation as Sound[] (array) — take [0].
      const sounds: Sound[] = []
      for (const row of trendingRows) {
        const soundOrArray = row.sound as Sound | Sound[] | null
        const sound = Array.isArray(soundOrArray) ? soundOrArray[0] ?? null : soundOrArray
        if (sound && sound.id) {
          sounds.push(sound)
        }
      }

      if (sounds.length === 0) {
        return fetchFallbackSounds(limit)
      }

      return sounds
    })
  },
}

async function fetchFallbackSounds(limit: number): Promise<Sound[]> {
  if (!supabase) throw new Error('Supabase client is not initialized')

  const { data, error } = await supabase
    .from('sounds')
    .select(LIST_COLUMNS)
    .eq('is_active', true)
    .or(EXCLUDE_MATURE_FILTER)
    .order('downloads', { ascending: false })
    .limit(limit)

  if (error) {
    throw new SupabaseError(
      `Trending fallback fetch failed: ${error.message}`,
      error,
    )
  }

  return (data as Sound[]) ?? []
}

